import { useEffect, useState } from 'react';
import api from '../lib/api';

interface Win {
  id: string;
  timeframe: string;
  title: string;
  description: string;
  status: string;
  progress: number;
  currentValue: number | null;
  targetValue: number | null;
  points: number;
  completedAt: string | null;
}

interface WinsScore {
  totalPoints: number;
  currentStreak: number;
  longestStreak: number;
  level: number;
  quickWins: number;
  dailyWins: number;
  weeklyWins: number;
  monthlyWins: number;
}

const TIMEFRAME_CONFIG: Record<string, { icon: string; color: string; bg: string; border: string }> = {
  QUICK: { icon: '🎯', color: 'text-orange-600', bg: 'bg-orange-50', border: 'border-orange-200' },
  DAILY: { icon: '⭐', color: 'text-blue-600', bg: 'bg-blue-50', border: 'border-blue-200' },
  WEEKLY: { icon: '🏆', color: 'text-purple-600', bg: 'bg-purple-50', border: 'border-purple-200' },
  MONTHLY: { icon: '🥇', color: 'text-amber-600', bg: 'bg-amber-50', border: 'border-amber-200' },
  YEARLY: { icon: '👑', color: 'text-emerald-600', bg: 'bg-emerald-50', border: 'border-emerald-200' },
};

const LEVELS = ['Novice', 'Apprentice', 'Practitioner', 'Expert', 'Master'];

interface WinsPanelProps {
  mundusId: string;
}

export default function WinsPanel({ mundusId }: WinsPanelProps) {
  const [activeWins, setActiveWins] = useState<Win[]>([]);
  const [recentCompleted, setRecentCompleted] = useState<Win[]>([]);
  const [score, setScore] = useState<WinsScore | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadWins();
  }, [mundusId]);

  const loadWins = async () => {
    try {
      // Use the mundus detail which includes wins via API or query directly
      const [winsRes] = await Promise.all([
        api.get(`/mundus/${mundusId}`),
      ]);

      // For now, parse from mundus response or call a dedicated endpoint
      // The wins data comes from the backend wins-engine getWinsSummary
      const mundus = winsRes.data.data || winsRes.data;

      // Try dedicated wins endpoint (we'll add it)
      try {
        const winsData = await api.get(`/mundus/${mundusId}/wins`);
        setActiveWins(winsData.data.activeWins || []);
        setRecentCompleted(winsData.data.recentCompleted || []);
        setScore(winsData.data.score || null);
      } catch {
        // Endpoint may not exist yet - use empty state
        setActiveWins([]);
        setRecentCompleted([]);
        setScore(null);
      }
    } catch (error) {
      console.error('Failed to load wins:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="bg-white rounded-xl border border-slate-200 p-4 animate-pulse">
        <div className="h-4 bg-slate-200 rounded w-24 mb-3" />
        <div className="h-8 bg-slate-100 rounded" />
      </div>
    );
  }

  return (
    <div className="bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden">
      {/* Score Header */}
      <div className="bg-gradient-to-r from-blue-600 to-purple-600 px-4 py-3 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="text-white">
            <div className="text-xs opacity-80">Level {score?.level || 1}</div>
            <div className="font-bold text-lg">{score?.totalPoints || 0} pts</div>
          </div>
        </div>
        <div className="flex items-center gap-4 text-white text-sm">
          {score?.currentStreak ? (
            <div className="flex items-center gap-1">
              <span>🔥</span>
              <span className="font-medium">{score.currentStreak} day streak</span>
            </div>
          ) : null}
          <div className="px-2 py-0.5 bg-white/20 rounded-full text-xs font-medium">
            {LEVELS[(score?.level || 1) - 1] || 'Novice'}
          </div>
        </div>
      </div>

      {/* Active Wins */}
      <div className="p-4">
        {activeWins.length === 0 && recentCompleted.length === 0 ? (
          <div className="text-center py-4 text-slate-400 text-sm">
            <span className="text-2xl block mb-2">🎯</span>
            Start chatting with your agent to unlock wins!
          </div>
        ) : (
          <div className="space-y-3">
            {/* Active wins with progress bars */}
            {activeWins.map(win => {
              const config = TIMEFRAME_CONFIG[win.timeframe] || TIMEFRAME_CONFIG.DAILY;
              return (
                <div key={win.id} className={`p-3 rounded-lg ${config.bg} border ${config.border}`}>
                  <div className="flex items-center justify-between mb-1.5">
                    <div className="flex items-center gap-2">
                      <span className="text-lg">{config.icon}</span>
                      <span className={`text-sm font-medium ${config.color}`}>{win.title}</span>
                    </div>
                    <span className="text-xs text-slate-500">
                      {win.currentValue || 0}/{win.targetValue || 1}
                    </span>
                  </div>
                  {/* Progress bar */}
                  <div className="w-full h-2 bg-white/50 rounded-full overflow-hidden">
                    <div
                      className={`h-full rounded-full transition-all duration-500 ${
                        win.progress >= 100 ? 'bg-green-500' : 'bg-blue-500'
                      }`}
                      style={{ width: `${Math.min(win.progress, 100)}%` }}
                    />
                  </div>
                  <div className="text-xs text-slate-500 mt-1">{win.description}</div>
                </div>
              );
            })}

            {/* Recent completed wins */}
            {recentCompleted.length > 0 && (
              <div className="pt-2 border-t border-slate-100">
                <div className="text-xs text-slate-500 mb-2 font-medium">Recent Wins</div>
                {recentCompleted.slice(0, 3).map(win => {
                  const config = TIMEFRAME_CONFIG[win.timeframe] || TIMEFRAME_CONFIG.DAILY;
                  return (
                    <div key={win.id} className="flex items-center gap-2 py-1.5 text-sm">
                      <span>✅</span>
                      <span className="text-slate-700">{win.title}</span>
                      <span className="ml-auto text-xs text-slate-400">+{win.points}pts</span>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
